// ShareProfileDialog.tsx — "Share Profile" sheet.
//
// 1.24.00 E1 brought this up to the post sheet's bar. It used to be a URL box, a
// hardcoded 7×7 bit grid that LOOKED like a QR and encoded nothing, and two
// aria-hidden "coming soon" rows. All three are gone:
//
//   1. Preview  — a real, non-interactive rendering of the profile as it will
//                 look once shared: avatar, name (+ verified mark), @handle,
//                 bio clamped, and the counts the profile header itself shows.
//   2. Share to — a tile grid: Copy link, the device's own share sheet (ONLY
//                 when navigator.share exists — no control wired to nothing),
//                 and one real anchor per platform (X / WhatsApp / Telegram /
//                 Facebook / Reddit / Email). Discord has no share URL.
//   3. Message  — an optional ≤280-char note that rides in every platform href
//                 and in the native payload. Copy link copies the URL ONLY.
//   4. QR       — the REAL one (E3): rendered by the server with segno and
//                 delivered as an SVG string, shown through an <img> with a
//                 data: URI and a real alt. Never dangerouslySetInnerHTML.
//   5. Invite   — the 1.24.00 B2 personal /join/<code> tile, own-profile only.
//   6. Footer   — ghost Done + primary Copy link.
//
// The URL comes from core/lib/postUrl.ts's buildProfileUrl — the same helper
// ProfileActions' "Copy link to profile" uses, so the two can never disagree
// and neither can hand out an unencoded handle.

import { useId, useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button, Avatar, TierBadge, deriveTier, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useInviteLink, useProfileQr } from '@/hooks';
import { buildJoinUrl, buildProfileUrl } from '@/core/lib/postUrl';
import { buildPlatformTargets } from '@/core/lib/shareTargets';
import {
  ShareMessageField,
  SharePlatformTile,
  ShareTileAction,
} from '@/components/share/ShareSheetParts';
import { ShareSectionLabel, ShareTileGrid } from '@/components/share/shareChrome';
import { formatCount } from '@/components/formatCount';
import type { User } from '@/core/models';

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface ShareProfileDialogProps {
  /** The profile being shared — the preview renders it, so the whole record is
   *  needed, not just the handle. */
  user: User;
  /**
   * Whose profile this is. An invite link belongs to the person whose profile
   * is on screen, so the "Copy invite link" tile is OWN-PROFILE ONLY — on
   * someone else's profile it showed the VIEWER's personal code, which reads
   * as "invite people to @them" and hands out the wrong link. Required (not
   * defaulted) so a new call site has to answer the question.
   */
  isOwn: boolean;
  onClose: () => void;
}

// ─── Styled components ─────────────────────────────────────────────────────────

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// — Preview —

const Preview = styled.section`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
`;

const PreviewHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HandleLine = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Bio = styled.p`
  margin: 8px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Counts = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
`;

const StatValue = styled.strong`
  color: ${({ theme }) => theme.color.textPrimary};
`;

// The link itself, still visible. The post sheet has no equivalent, but this
// dialog always did, and taking it away would mean a member can no longer SEE
// what Copy link is about to put on their clipboard. Display only — selectable,
// not a control.
const UrlDisplay = styled.p`
  margin: 0;
  padding: 9px 12px;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: all;
`;

// — QR —

const QrSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

// White plate behind the code — a QR scanner needs the quiet zone light in both
// themes, and the SVG segno emits is transparent-on-dark otherwise. Same plate
// the 2FA dialog uses.
const QrPlate = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
`;

const QrImage = styled.img`
  width: 160px;
  height: 160px;
`;

// — Footer —

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// 1.24.00 B2 — the invite tile. Unchanged by E1.
const InviteTile = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
  text-align: left;
  &:hover:not(:disabled) { filter: brightness(1.12); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.focusRing}; outline-offset: 2px; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const InviteText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const InviteSub = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * The invite tile, in its own component so `useInviteLink()` (a real
 * `GET /users/me/invite/`) is never even CALLED while looking at someone
 * else's profile — a conditional hook would be illegal, an unconditional one
 * would fire a pointless authenticated request on every share of a stranger.
 */
function OwnInviteTile() {
  const toast = useToast();
  const { invite, loading } = useInviteLink();
  const inviteUrl = invite ? buildJoinUrl(invite.code) : null;

  async function handleCopyInviteLink() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast('Invite link copied');
    } catch {
      toast('Could not copy link');
    }
  }

  // Renders only once the code is known — a Copy button with nothing to copy
  // would be an inert control.
  if (!inviteUrl) return null;

  return (
    <InviteTile type="button" onClick={() => void handleCopyInviteLink()} disabled={loading}>
      <Icon name="user-plus" size={16} />
      <InviteText>
        <span>Copy invite link</span>
        <InviteSub title={inviteUrl}>{inviteUrl}</InviteSub>
      </InviteText>
    </InviteTile>
  );
}

export function ShareProfileDialog({ user, isOwn, onClose }: ShareProfileDialogProps) {
  const toast = useToast();
  const shareToLabelId = useId();
  const [message, setMessage] = useState('');
  const { qrSvg } = useProfileQr(user.handle);

  const url = buildProfileUrl(user.handle);
  // The same shape the post sheet and the server's og:title already use.
  const title = `${user.name} (${user.handle}) on TruXSocial`;
  // Read once per render — a device either has the Web Share API or it doesn't.
  const canNativeShare = typeof navigator.share === 'function';
  const targets = buildPlatformTargets(url, title, message);

  const following = user.stats?.following ?? 0;
  const followers = user.stats?.followers ?? 0;
  const posts = user.stats?.posts ?? 0;

  function handleCopyLink() {
    // navigator.clipboard is undefined outside a secure context (plain http, some
    // embedded webviews) — guard rather than let that throw synchronously.
    if (!navigator.clipboard) {
      toast('Could not copy link');
      return;
    }
    void navigator.clipboard.writeText(url).then(
      () => toast('Profile link copied'),
      () => toast('Could not copy link'),
    );
  }

  function handleNativeShare() {
    const data: ShareData = { title, url, ...(message ? { text: message } : {}) };
    void navigator.share(data).then(undefined, (err: unknown) => {
      // The user closing their own share sheet is not an error worth a toast.
      if ((err as { name?: unknown } | null)?.name === 'AbortError') return;
      toast('Could not share');
    });
  }

  return (
    <Dialog open title="Share Profile" onClose={onClose}>
      <Stack>
        {/* 1. Preview — display only */}
        <Preview aria-label="Preview" data-testid="share-profile-preview">
          <PreviewHead>
            <Avatar name={user.name} size={44} avatarKey={user.avatarKey} />
            <PreviewMeta>
              <NameLine>
                <Name>{user.name}</Name>
                <TierBadge tier={deriveTier(user.verified)} />
              </NameLine>
              <HandleLine>{user.handle}</HandleLine>
            </PreviewMeta>
          </PreviewHead>
          {user.bio && <Bio>{user.bio}</Bio>}
          <Counts>
            <Stat>
              <StatValue>{formatCount(followers)}</StatValue>
              Followers
            </Stat>
            <Stat>
              <StatValue>{formatCount(following)}</StatValue>
              Following
            </Stat>
            <Stat>
              <StatValue>{formatCount(posts)}</StatValue>
              Posts
            </Stat>
          </Counts>
        </Preview>

        <UrlDisplay data-testid="profile-url" title={url}>
          {url}
        </UrlDisplay>

        {/* 2. Share to */}
        <div>
          <ShareSectionLabel id={shareToLabelId}>Share to</ShareSectionLabel>
          <ShareTileGrid role="group" aria-labelledby={shareToLabelId} data-testid="share-targets">
            <ShareTileAction icon="link" label="Copy link" onClick={handleCopyLink} />
            {canNativeShare && (
              <ShareTileAction icon="share" label="Share via…" onClick={handleNativeShare} />
            )}
            {targets.map((t) => (
              <SharePlatformTile key={t.key} target={t} />
            ))}
          </ShareTileGrid>
        </div>

        {/* 3. Optional message */}
        <ShareMessageField
          value={message}
          onChange={setMessage}
          placeholder={`Say something about ${user.handle}`}
        />

        {/* 4. The REAL QR (E3). Absent entirely until the server answers — a
            broken image where a scannable code should be is worse than none. */}
        {qrSvg && (
          <QrSection data-testid="profile-qr-section">
            <ShareSectionLabel>Scan to open</ShareSectionLabel>
            <QrPlate>
              <QrImage
                data-testid="profile-qr"
                alt={`QR code linking to ${user.handle}'s profile`}
                src={`data:image/svg+xml;utf8,${encodeURIComponent(qrSvg)}`}
              />
            </QrPlate>
          </QrSection>
        )}

        {/* 5. Real: the member's OWN personal invite link (1.24.00 B2). */}
        {isOwn && <OwnInviteTile />}

        {/* 6. Footer */}
        <ButtonRow>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
          <Button variant="primary" onClick={handleCopyLink}>
            Copy link
          </Button>
        </ButtonRow>
      </Stack>
    </Dialog>
  );
}
